import React, { useReducer, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { updateLoadError } from "../../redux-store/currentUserSlice";
import { useSelector, useDispatch } from "react-redux";
import { Signup, GoogleSignup } from "../../redux-store/currentUserActions";
import { GoogleLogin } from "@react-oauth/google";
import "../../styles/App.scss"
import "./index.css";
import { InputAdornment, CircularProgress } from '@mui/material';
import { Typography, Box, Button, IconButton, Container, Grid, Card, InputLabel, TextField, FormControlLabel, Checkbox, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import { Visibility, VisibilityOff, FiberManualRecord as FiberManualRecordIcon } from "@mui/icons-material";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { FREE_EMAIL_DOMAINS } from "config/constants";
import { appConfig } from "redux-store/appConfigActions";
import { MixpanelService } from "utils/mixpanelService";

const SignUpForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invitationToken = queryParams.get('token');
  const startAIQuery = queryParams.get("startAIQuery");
  if (startAIQuery) {
    localStorage.setItem("startAIQuery", startAIQuery);
    queryParams.delete("startAIQuery");
    history.replace({
      pathname: location.pathname,
      search: queryParams.toString()
    });
  }
  const { openSnackbar } = useSnackbar();
  const [allowOnlyCompanyEmails, setAllowOnlyCompanyEmails] = useState(true);

  const [state, setState] = useReducer(reducer, {
    name: "",
    email: "",
    password: "",
    token: "",
    isLoading: false,
    errors: {},
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const redirectPath = urlParams.get('redirect');

    if (tokenFromUrl) {
      setState({
        type: "SET_STATE",
        payload: { token: tokenFromUrl },
      });
    }

    if (redirectPath) {
      setState({
        type: "SET_STATE",
        payload: { redirectPath: decodeURIComponent(redirectPath) },
      });
    }
    checkAppConfig();
  }, []);

  const checkAppConfig = async () => {
    try {
      const res = await dispatch(appConfig({})).unwrap();
      setAllowOnlyCompanyEmails(res.allow_only_company_emails);
    } catch (error) {
      console.error('Failed to fetch app config:', error);
    }
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const responseMessage = async (response) => {
    try {
      setState({
        type: "SET_STATE",
        payload: { isLoading: true },
      });

      if (allowOnlyCompanyEmails) {
        const tokenData = JSON.parse(atob(response.credential.split('.')[1]));
        const emailDomain = tokenData.email.split('@')[1].toLowerCase().trim();
        const separatedEmailDomain = emailDomain.split('.');
        if (FREE_EMAIL_DOMAINS.includes(separatedEmailDomain[0])) {
          openSnackbar({
            message: "Please use your company email address to Sign Up.",
            severity: 'error'
          });
          return;
        }
      }

      const res = await dispatch(GoogleSignup({ credential: response.credential, invitationToken: invitationToken }));

      if (!res.payload) {
        throw new Error('No response from server');
      }

      if (res.payload.message === 'Signed up successfully' || res.payload.message === 'Logged in successfully') {
        MixpanelService.track(res.payload.message === "Signed up successfully" ? "signup_success" : "login_success", { method: "google" });
        if (res.payload.has_organisation_or_project) {
          history.push("/quick-start");
        } else {
          history.push("/home");
        }
      } else {
        openSnackbar({
          message: res.payload.message || 'Signup failed. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      openSnackbar({
        message: error.message || 'An error occurred during signup. Please try again.',
        severity: 'error'
      });
    } finally {
      setState({
        type: "SET_STATE",
        payload: { isLoading: false },
      });
    }
  };

  const errorMessage = (error) => {
    dispatch(updateLoadError(error));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState({
      type: "SET_STATE",
      payload: { [name]: value },
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!state.email) {
      errors["email"] = "Please enter an email address.";
    } else if (!/\S+@\S+\.\S+/.test(state.email)) {
      errors["email"] = "Please enter a valid email address.";
    } else {
      if (allowOnlyCompanyEmails) {
        const emailDomain = state.email.split('@')[1].toLowerCase().trim();
        const separatedEmailDomain = emailDomain.split('.');
        if (FREE_EMAIL_DOMAINS.includes(separatedEmailDomain[0])) {
          errors["email"] = "Please use your company email address to Sign Up.";
        }
      }
    }

    if (!state.name || state.name.length < 3) {
      errors["name"] = "Name must be at least 3 characters long.";
    }

    if (!state.password || state.password.length < 6) {
      errors["password"] = "Password must be at least 6 characters long.";
    }

    return errors;
  };

  const handleSubmit = async () => {
    MixpanelService.track("signup_submitted", {});
    setState({
      type: "SET_STATE",
      payload: { isLoading: true },
    });

    const payload = {
      ...state,
      ...({ token: state.token ? state.token : null })
    };

    const res = await dispatch(Signup(payload));

    setState({
      type: "SET_STATE",
      payload: { isLoading: false },
    });

    if (res.payload && (res.payload.message === 'User created successfully' || res.payload.message === 'User added to the project successfully')) {
      MixpanelService.track("signup_success", {});
      if (!state.token) {
        localStorage.setItem('showEmailSentMessage', 'true');
      }

      if (state.redirectPath) {
        const pathToRedirect = state.redirectPath;

        setState({
          type: "SET_STATE",
          payload: { redirectPath: '' },
        });

        history.push(pathToRedirect);
      } else {
        history.push("/login");
      }
    } else {
      const errorMessage = res.payload ? (res.payload.error ? res.payload.error : res.payload.errors?.join(', ')) : "Sign up failed. Please try again.";
      setState({
        type: "SET_STATE",
        payload: {
          isLoading: false,
          loginError: errorMessage,
        },
      });

      openSnackbar({ message: errorMessage, severity: 'error' });
    }
  };

  const onFormSubmit = async () => {
    const errors = validateForm();
    setState({
      type: "SET_STATE",
      payload: { errors },
    });

    if (Object.keys(errors).length === 0) {
      await handleSubmit();
    } else {
      setState({
        type: "SET_STATE",
        payload: {
          isLoading: false,
          loginError: "Please correct the errors before proceeding.",
        },
      });
    }
  };

  const _errors = useSelector((state) => state.user.errors);
  var email_error = "";
  var name_error = "";

  if (
    _errors &&
    _errors !== "Unvalidated User" &&
    Object.keys(_errors).length > 0
  ) {
    _errors?.forEach((err) => {
      if (err.split(" ")[0] === "Email") {
        email_error = err;
      }
      if (err.split(" ")[0] === "name") {
        name_error = err;
      }
    });
  }

  return (
    <div className="bg-[var(--background-secondary)]">
      <Container maxWidth="md" className="full-height p-4 bg-[var(--background-secondary)]">
        <Grid container spacing={2} className="d-flex justify-content-center align-items-center h-100">
          <Grid item xs={12} md={6} className="column-margin">
            <Card className="grid-card customBorder">
              <div className="right-column">
                <Box className="heading-container">
                  <Box className="create-account-title">
                    Create Qodex Account
                  </Box>
                  <Box className="signin-link-container flex items-center justify-content-center">
                    or
                    <div
                      className="signin-link cursor-pointer text-[#6241D4] underline"
                      onClick={() => history.push('/login')}
                    >
                      Sign In
                    </div>
                  </Box>
                </Box>
                <Box>
                  <InputLabel htmlFor="name" className="input-label mt-3">Name</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    className="input-field"
                    variant="outlined"
                    name="name"
                    placeholder="John Doe"
                    value={state.name}
                    onChange={handleInputChange}
                    error={Boolean(state.errors["name"] || name_error)}
                    helperText={state.errors["name"] || name_error}
                  />

                  <InputLabel htmlFor="email" className="input-label mt-3">Email</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    className="input-field"
                    variant="outlined"
                    name="email"
                    value={state.email}
                    onChange={handleInputChange}
                    error={Boolean(state.errors["email"] || email_error)}
                    helperText={state.errors["email"] || email_error}
                    placeholder="yourname@company.com"
                  />

                  <InputLabel htmlFor="password" className="input-label mt-3">Password</InputLabel>
                  <TextField
                    fullWidth
                    size="small"
                    className="input-field"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={state.password}
                    onChange={handleInputChange}
                    error={Boolean(state.errors["password"])}
                    helperText={state.errors["password"] || ' '}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box className="mb-2 mt-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="updatesOptIn"
                        color="primary"
                        checked={state.updatesOptIn}
                      />
                    }
                    label={
                      <Typography className='text-color'>
                        Sign up to get product updates, news, and marketing communications.
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="staySignedIn"
                        color="primary"
                        checked={state.staySignedIn}
                      />
                    }
                    label={
                      <Typography className='text-color'>
                        Stay signed in for 30 days.
                      </Typography>
                    }
                  />
                </Box>
                <div className="btn_login">
                  <Button
                    variant="contained"
                    onClick={onFormSubmit}
                    size="medium"
                    style={{ padding: '10px 0', width: '100%' }}
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
                <Box className="mb-4 mt-4 center">
                  By creating an account, I agree to the{" "}
                  <a href="https://www.qodex.ai/termsandconditions" className="terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>.
                </Box>
                <Box className="line-container primary-color mb-4">
                  <span className="cutting-line"></span>
                  <Typography className="overlay-text">or</Typography>
                  <span className="cutting-line"></span>
                </Box>
                <div style={{ width: "100%" }}>
                  <GoogleLogin
                    buttonText="Sign up with Google"
                    onSuccess={responseMessage}
                    onError={errorMessage}
                    shape="rectangular"
                    style={{ width: "100%", color: "rgb(0, 68, 239)" }}
                    disabled={state.isLoading}
                  />
                </div>
              </div>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}

export default SignUpForm;

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_STATE": {
      return {
        ...state,
        ...action.payload,
      };
    }
    default:
      return state;
  }
};